/**
 * A searchable language select — docs/31-global-languages.md.
 *
 * Fifty languages do not fit a row of chips or a bare <select>: the owner
 * called the console's tabs "ugly", and nobody should scroll past forty
 * names to find their own. So: a button showing the current language in its
 * own script, opening a list with a search field at the top that matches the
 * native name, the English name and the code ("telugu", "తెలు", "te").
 *
 * The same control serves a person choosing their own language (the
 * consoles' Profile; an org's resident form) and the admin reviewing
 * translations. Callers pass the options already grouped and ordered;
 * `languageOptions` builds the usual ones from the enabled list.
 *
 * Keyboard: type to filter, ↑/↓ to move, Enter to choose, Escape to close.
 * WAI-ARIA combobox with a listbox popup. Every option is at least 44px tall.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_ENABLED_CODES,
  LOCALES,
  localeDef,
  localeSearchRank,
  pickerLocales,
} from '@drbell/i18n/locales';
import { localeDirection } from '@drbell/i18n/direction';

export interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  /** Heading the option sits under; consecutive options share it. */
  group?: string;
  /** Shown faded, with `note`: a language not offered, kept because it is the current one. */
  muted?: boolean;
  note?: string;
}

/** The enabled languages as `GET /api/i18n/locales` returns them. */
export interface EnabledLanguage {
  code: string;
  name: string;
  nativeName: string;
}

/** Every language DrBell knows, offered or not, in registry order. */
export const ALL_LANGUAGES: EnabledLanguage[] = LOCALES.map(({ code, name, nativeName }) => ({ code, name, nativeName }));

/** Before the server has answered, or when it cannot: the nine. */
export const DEFAULT_LANGUAGES: EnabledLanguage[] = LOCALES.filter((l) => DEFAULT_ENABLED_CODES.includes(l.code)).map(
  ({ code, name, nativeName }) => ({ code, name, nativeName }),
);

/**
 * Fetch the enabled languages once per page load and share the answer. A
 * failure leaves the default nine, never an empty picker.
 */
let shared: Promise<EnabledLanguage[]> | null = null;
export function useEnabledLanguages(load?: () => Promise<{ locales: EnabledLanguage[] }>): EnabledLanguage[] {
  const [list, setList] = useState<EnabledLanguage[]>(DEFAULT_LANGUAGES);
  useEffect(() => {
    if (!load) return;
    let live = true;
    shared ??= load()
      .then((r) => (r.locales.length ? r.locales : DEFAULT_LANGUAGES))
      .catch(() => {
        shared = null;
        return DEFAULT_LANGUAGES;
      });
    void shared.then((l) => live && setList(l));
    return () => {
      live = false;
    };
  }, [load]);
  return list;
}

/**
 * A person's picker: the browser's language suggested first (when offered),
 * then every enabled language; the current one kept, marked, even if it is no
 * longer offered, so opening a form never silently changes a saved choice.
 */
export function languageOptions(
  enabled: readonly EnabledLanguage[],
  current: string,
  labels: { suggested?: string; all?: string; notOffered?: string } = {},
): LanguageOption[] {
  const device = typeof navigator !== 'undefined' ? navigator.language : null;
  const { suggested, rest } = pickerLocales(enabled, { deviceLocale: device });
  const out: LanguageOption[] = [];
  if (suggested) out.push({ ...pick(suggested), group: labels.suggested ?? 'Suggested' });
  for (const l of rest) out.push({ ...pick(l), group: suggested ? (labels.all ?? 'All languages') : undefined });
  if (current && !enabled.some((l) => l.code === current)) {
    const def = localeDef(current);
    out.push({
      code: current,
      name: def?.name ?? current,
      nativeName: def?.nativeName ?? current,
      group: labels.notOffered ?? 'Not offered at the moment',
      muted: true,
      note: 'not offered',
    });
  }
  return out;
}

/** A code's own name for a table cell: "తెలుగు"; the code itself if unknown. */
export function languageName(code: string): string {
  return localeDef(code)?.nativeName ?? code;
}

function pick(l: EnabledLanguage): LanguageOption {
  return { code: l.code, name: l.name, nativeName: l.nativeName };
}

export function LanguageSelect({
  value,
  onChange,
  options,
  label = 'Language',
  searchPlaceholder = 'Search languages',
  disabled,
  detail,
  className = '',
  wide,
}: {
  value: string;
  onChange: (code: string) => void;
  options: readonly LanguageOption[];
  /** The control's accessible name. */
  label?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Extra text on the right of an option, and on the button — e.g. progress. */
  detail?: (option: LanguageOption) => ReactNode;
  className?: string;
  /** A wider popup, for options with a detail. */
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  // Best matches first within each group, groups in their given order.
  const shown = useMemo(() => {
    const groups = [...new Set(options.map((o) => o.group ?? ''))];
    return options
      .map((o, i) => ({ o, i, rank: localeSearchRank(o, query) }))
      .filter((x) => x.rank >= 0)
      .sort((a, b) => groups.indexOf(a.o.group ?? '') - groups.indexOf(b.o.group ?? '') || a.rank - b.rank || a.i - b.i)
      .map((x) => x.o);
  }, [options, query]);
  const current = options.find((o) => o.code === value);
  const def = localeDef(value);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const at = options.findIndex((o) => o.code === value);
    setActive(at < 0 ? 0 : at);
    // After the popup renders.
    requestAnimationFrame(() => input.current?.focus());
  }, [open, options, value]);

  useEffect(() => {
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (o: LanguageOption | undefined) => {
    if (!o) return;
    onChange(o.code);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(shown.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(shown[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.nativeName ?? def?.nativeName ?? value}`}
        className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-(--line) bg-(--bg) px-3 text-left text-sm hover:border-(--accent) disabled:opacity-40"
      >
        <span lang={value} dir={localeDirection(value)} className="font-medium">{current?.nativeName ?? def?.nativeName ?? value}</span>
        <span className="font-mono text-xs text-(--muted)">{value}</span>
        {current && detail && <span className="ml-auto text-xs text-(--muted)">{detail(current)}</span>}
        <span aria-hidden className={`${current && detail ? '' : 'ml-auto'} text-(--muted)`}>▾</span>
      </button>

      {open && (
        <div
          className={`absolute left-0 z-30 mt-1 ${wide ? 'w-[min(32rem,90vw)]' : 'w-[min(22rem,90vw)]'} rounded-xl border border-(--line) bg-(--bg) shadow-lg`}
        >
          <div className="border-b border-(--line) p-2">
            <input
              ref={input}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKey}
              role="combobox"
              aria-expanded
              aria-controls={`${id}-list`}
              aria-activedescendant={shown[active] ? `${id}-${shown[active]!.code}` : undefined}
              aria-autocomplete="list"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              className="min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3 text-sm outline-none focus:border-(--accent)"
            />
          </div>
          <ul ref={list} id={`${id}-list`} role="listbox" aria-label={label} className="max-h-80 overflow-y-auto py-1">
            {shown.length === 0 && <li className="px-3 py-3 text-sm text-(--muted)">No language matches “{query}”</li>}
            {shown.map((o, i) => {
              const heading = o.group && o.group !== shown[i - 1]?.group ? o.group : null;
              const selected = o.code === value;
              return (
                <li key={o.code} role="presentation">
                  {heading && (
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-(--muted)">
                      {heading}
                    </div>
                  )}
                  <div
                    id={`${id}-${o.code}`}
                    data-index={i}
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(o)}
                    className={`flex min-h-11 cursor-pointer items-center gap-3 px-3 py-1.5 text-sm ${
                      i === active ? 'bg-(--accent-soft)' : ''
                    } ${o.muted ? 'opacity-60' : ''}`}
                  >
                    <span aria-hidden className="w-4 text-(--accent)">{selected ? '✓' : ''}</span>
                    <span className="min-w-0 flex-1">
                      <span lang={o.code} dir={localeDirection(o.code)} className={`block truncate ${selected ? 'font-semibold text-(--accent)' : ''}`}>
                        {o.nativeName}
                      </span>
                      <span className="block truncate text-xs text-(--muted)">
                        {o.name} · <span className="font-mono">{o.code}</span>
                        {o.note ? ` · ${o.note}` : ''}
                      </span>
                    </span>
                    {detail && <span className="shrink-0 text-xs text-(--muted)">{detail(o)}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
